import React, { useState } from 'react';
import { NativeSyntheticEvent, TextInputKeyPressEventData } from 'react-native';
import { translate } from '../../../services/translate';
import Toast from 'react-native-toast-message';
import DeleteModal from '../../../components/DeleteModal';
import { Pagination } from '../../../components/Pagination';
import { dispatchService } from '../../../services/central-api/dispatch';
import { ClientError } from '../../../services/central-api/base-service';
import { RouteProp, useIsFocused, useRoute } from '@react-navigation/native';
import { DispatchParamList } from '../../../typings/Params';
import useFilters from '../../../components/Filter/hooks/useFilters';
import FilterInput from '../../../components/Filter/components/FilterInput';
import MyDataTable from '../../../components/MyDataTable';
import CustomButton from '../../../components/CustomButton';
import UnitPauseReasonsModal from './UnitPauseReasonsModal';
import { useAuth } from '../../../context/Auth';
import { minutesToText } from '../../../services/time-helpers';
import Filter from './Filters';
import DropdownButton from '../../../components/DropdownButton';
import { useQuery } from '@tanstack/react-query';
import Container from '../../../components/Container';
import Header from '../../../components/Header';
import Card from '../../../components/Card';
import DataTableHeader from '../../../components/DataTableHeader';
import DataTableContainer from '../../../components/DataTableContainer';
import { RootNavigation } from '../../../Routes';


export default function UnitPauseReasons({ navigation }: { navigation: RootNavigation; }) {
    const route = useRoute<RouteProp<DispatchParamList, 'UnitPauseReasons'>>();
    const { user: authenticatedUser } = useAuth();


    const [filters, setFilters] = useFilters<DispatchParamList['UnitPauseReasons']>({
        ...route.params,
        page: Math.max(route.params.page - 1, 0),
        limit: route.params.limit ?? 25,
    });
    const [textFilter, setTextFilter] = useState(filters.textFilter ?? '');
    const [isModalVisible, setModalVisible] = useState<boolean>(false);
    const [selectedUnitPauseReasons, setSelectedUnitPauseReasons] = useState<UnitPauseReason>();
    const [isDeleteModalVisible, setDeleteModalVisible] = useState(false);

    const isFocused = useIsFocused();
    const queryResult = useQuery({
        queryKey: ['getPaginatedUnitPauseReasons', filters],
        queryFn: () => dispatchService.getPaginatedUnitPauseReasons(filters),
        enabled: !!authenticatedUser && isFocused,
    });


    const isEnabled = !!authenticatedUser;
    const isLoading = isEnabled && queryResult.isFetching;

    const { data, refetch } = queryResult;

    async function confirmDelete(unitPauseReason: UnitPauseReason) {
        try {
            if (unitPauseReason?.id) {
                await dispatchService.deleteUnitPauseReason(unitPauseReason.id);
                Toast.show({
                    type: 'sentinelxSuccess',
                    text1: translate('ActionSuccessfully'),
                });
                setDeleteModalVisible(false);
                refetch();


            }
        } catch (err) {
            if (err instanceof ClientError) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate(err.message),
                });
            }


            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }


    function handleKeyDown(e: NativeSyntheticEvent<TextInputKeyPressEventData>) {
        if (e.nativeEvent.key == 'Enter') {
            navigation.setParams({
                ...filters,
                page: 1,
                textFilter: textFilter !== '' ? textFilter : undefined
            });
            setFilters(old => ({
                ...old,
                page: 0,
                textFilter: textFilter !== '' ? textFilter : undefined
            }));
        }
    }


    function handleFormClose() {
        setSelectedUnitPauseReasons(undefined);
        setModalVisible(false);
        refetch();
    }


    return (
        <Container>
            <Header
                title={translate('unitPauseReasons')}
                actions={
                    <CustomButton
                        text={translate('register')}
                        onPress={() => {
                            setSelectedUnitPauseReasons(undefined);
                            setModalVisible(true);
                        }}
                    />
                }
            />
            <Card>
                <DataTableHeader
                    zIndex={2}
                    title={translate('registeredUnitPauseReasons')}
                    filter={
                        <Filter
                            filters={filters}
                            setFilters={setFilters}
                            textFilter={textFilter}
                            setTextFilter={setTextFilter}
                        />
                    }
                    filterInput={
                        <FilterInput
                            value={textFilter}
                            placeholder={translate('typeToSearch')}
                            onChange={setTextFilter}
                            onKeyPress={handleKeyDown}
                        />
                    }
                />


                <DataTableContainer
                    dataTable={
                        <MyDataTable
                            progressPending={isLoading}
                            columns={[
                                {
                                    name: 'ID',
                                    selector: row => row.id,
                                    grow: 0
                                },
                                {
                                    name: translate('name'),
                                    selector: row => row.name,
                                    wrap: true,
                                    grow: 1
                                },
                                {
                                    name: translate('definedPeriod'),
                                    selector: row => minutesToText(row.pauseMinutes, { longFormat: true }),
                                    wrap: true,
                                    grow: 1
                                },
                                {
                                    name: translate('actingBody'),
                                    selector: row => row.actingBody.name ?? '',
                                    wrap: true,
                                },
                                {
                                    name: translate('unit'),
                                    selector: row => row.actingBodyUnit?.name ?? '',
                                    wrap: true,
                                    grow: 1
                                },
                                {
                                    cell: row => (
                                        <DropdownButton
                                            actions={[
                                                {
                                                    content: translate('editInformation'),
                                                    onPress: () => {
                                                        setSelectedUnitPauseReasons(row);
                                                        setModalVisible(true);
                                                    }
                                                },
                                                {
                                                    content: translate('delete'),
                                                    variant: 'danger',
                                                    onPress: () => {
                                                        setSelectedUnitPauseReasons(row);
                                                        setDeleteModalVisible(true);
                                                    },
                                                }
                                            ]}
                                        />
                                    ),
                                    wrap: true,
                                    button: true
                                }
                            ]}
                            data={data?.rows ?? []}
                        />
                    }
                    pagination={
                        <Pagination
                            totalItems={data?.count ?? 0}
                            currentPage={filters.page}
                            pageSize={filters.limit}
                            setPageSize={pageSize => {
                                setFilters(old => ({
                                    ...old,
                                    limit: pageSize,
                                    page: 0,
                                }));
                                navigation.setParams({
                                    ...route.params,
                                    limit: pageSize,
                                    page: 1,
                                });
                            }}
                            setPage={page => {
                                setFilters(old => ({
                                    ...old,
                                    page
                                }));
                                navigation.setParams({
                                    ...route.params,
                                    page: page + 1
                                });
                            }}
                        />
                    }
                />
            </Card>


            <UnitPauseReasonsModal
                onClose={handleFormClose}
                authenticatedUser={authenticatedUser}
                unitPauseReason={selectedUnitPauseReasons}
                isModalVisible={isModalVisible}
            />


            {selectedUnitPauseReasons &&
                <DeleteModal
                    setModalVisible={setDeleteModalVisible}
                    isModalVisible={isDeleteModalVisible}
                    itemName={selectedUnitPauseReasons.name}
                    onSubmit={async () => {
                        await confirmDelete(selectedUnitPauseReasons);
                    }}
                />
            }
        </Container>
    );
}



